import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'sent'>('idle');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setState('loading');
    try { await sendPasswordResetEmail(firebaseAuth, email.trim()); }
    finally { setState('sent'); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-navy-800">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-700 via-navy-800 to-navy-900" />
        <div className="absolute -top-28 -right-20 w-80 h-80 rounded-full border border-white/10" />
        <div className="absolute -bottom-36 -left-24 w-96 h-96 rounded-full border border-gold-400/20" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <img src="/assets/logo2.png" alt="EzyAbroad" className="h-11 w-auto mb-8" />
          <h2 className="text-4xl font-bold text-white leading-tight">Get back to guiding<br />student journeys.</h2>
          <p className="mt-4 text-white/70 max-w-md leading-relaxed">Enter your registered email and we will send secure instructions to reset your password.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 md:px-12 py-12">
        <div className="w-full max-w-md">
          <button type="button" onClick={() => navigate('/consultancy/login')} className="mb-8 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-navy-600 cursor-pointer">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </button>
          {state === 'sent' ? (
            <div className="text-center sm:text-left">
              <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mb-6 mx-auto sm:mx-0"><CheckCircle2 className="w-7 h-7" /></div>
              <h1 className="text-3xl font-bold text-navy-600">Check your inbox</h1>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">If an account exists for <span className="font-medium text-gray-700">{email}</span>, password reset instructions have been sent.</p>
              <button type="button" onClick={() => navigate('/consultancy/login')} className="mt-8 w-full py-3.5 bg-navy-600 text-white font-semibold rounded-lg hover:bg-navy-700 transition-colors cursor-pointer">Return to Sign In</button>
              <button type="button" onClick={() => setState('idle')} className="mt-4 w-full text-sm text-gold-600 hover:text-gold-500 cursor-pointer">Try another email</button>
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-8">Consultancy Portal</div>
              <h1 className="text-3xl font-bold text-navy-600">Forgot your password?</h1>
              <p className="mt-2 text-sm text-gray-500">We will help you securely regain access to your workspace.</p>
              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-navy-600 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@consultancy.com" required autoFocus className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400" />
                  </div>
                </div>
                <button type="submit" disabled={state === 'loading'} className="w-full py-3.5 bg-navy-600 text-white font-semibold rounded-lg hover:bg-navy-700 disabled:opacity-70 transition-colors cursor-pointer">{state === 'loading' ? 'Sending instructions...' : 'Send Reset Instructions'}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
